import type { TaskStatus } from "@/components/task/TaskCheckbox.vue";

/** A value the mapped status column can hold, with its display name. */
export type StatusValueOption = { value: string; label: string };

/**
 * Editing state of a status mapping, independent of the column it applies to:
 * the value written back for each Routine status, and how the values Routine
 * does not write read back into a status.
 */
export type StatusValueMappingState = {
  writes: Partial<Record<TaskStatus, string>>;
  reads: Record<string, TaskStatus>;
};

/** Statuses in the order they are offered, most common first. */
export const mappedStatuses: TaskStatus[] = ["done", "todo", "discarded"];

export const emptyStatusValueMapping = (): StatusValueMappingState => ({
  writes: {},
  reads: {},
});

/**
 * The status a value is pinned to by being written for it: writing "Done" when
 * a task completes forces rows holding "Done" to read back as done, so that
 * direction is never editable on its own. When several statuses write the same
 * value (done and discarded usually do), the first of {@link mappedStatuses}
 * wins.
 */
export const writeStatusFor = (
  state: StatusValueMappingState,
  value: string
): TaskStatus | null =>
  mappedStatuses.find((status) => state.writes[status] === value) ?? null;

/** Todo is the default: every value reads back as some status. */
export const readStatusFor = (
  state: StatusValueMappingState,
  value: string
): TaskStatus => writeStatusFor(state, value) ?? state.reads[value] ?? "todo";

export const setWriteValue = (
  state: StatusValueMappingState,
  status: TaskStatus,
  value: string
): StatusValueMappingState => ({
  writes: { ...state.writes, [status]: value },
  reads: { ...state.reads },
});

export const setReadStatus = (
  state: StatusValueMappingState,
  value: string,
  status: TaskStatus
): StatusValueMappingState =>
  writeStatusFor(state, value) !== null
    ? state
    : {
        writes: { ...state.writes },
        reads: { ...state.reads, [value]: status },
      };

/** Clicking the status a value already reads as clears it back to todo. */
export const toggleReadStatus = (
  state: StatusValueMappingState,
  value: string,
  status: TaskStatus
): StatusValueMappingState =>
  setReadStatus(
    state,
    value,
    readStatusFor(state, value) === status ? "todo" : status
  );

/** Values whose read direction is still free, i.e. not pinned by a write. */
export const unpinnedValues = (
  state: StatusValueMappingState,
  options: StatusValueOption[]
): StatusValueOption[] =>
  options.filter((option) => writeStatusFor(state, option.value) === null);

export const isStatusMappingComplete = (
  state: StatusValueMappingState
): boolean =>
  mappedStatuses.every((status) => state.writes[status] !== undefined);

/** The values, among `values`, that read back as `status`. */
export const valuesReadAs = (
  state: StatusValueMappingState,
  values: string[],
  status: TaskStatus
): string[] => values.filter((value) => readStatusFor(state, value) === status);
